import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback } from 'react';
import { benefitsHiringCard } from '@ibk/shared-assets';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { ContentLayout, StepperCard } from '@ibk/shared-layout';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { Alert } from '@ibk/shared-ui';
import { AddressCard } from '../../../../components/AddressCard';
import { NewRechargeStep, NewRechargeStepStatus } from '../../types';
import { NewRechargeSidebar } from '../NewRechargeSidebar';
import styles from './DeliveryAddress.module.css';
export function DeliveryAddress({ deliveryLocation, formData, isLoading, isSavingAddress, isSimulating, onComplete, onReturn, onStepChange, onSaveAddress, onSelectAddress, onUpdateAddressSuccess, onUpdateAddressError, headerStepper, }) {
    const { t } = useTranslation();
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isMultipleAddressesEnabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.BENEFICIOS_MULTI_ADDRESSES);
    const breadcrumbs = [
        { label: t('recharge.breadcrumb.benefits'), href: '/beneficios' },
        { label: t('recharge.breadcrumb.new-recharge'), isActive: true },
    ];
    const currentStep = {
        id: NewRechargeStep.DELIVERY_ADDRESS,
        title: t('recharge.step.delivery-address'),
        number: 4,
        status: NewRechargeStepStatus.ACTIVE,
    };
    const handleBack = useCallback(() => {
        onReturn();
    }, [onReturn]);
    const handleNext = useCallback(() => {
        if (!deliveryLocation?.locationId) {
            console.error(t('recharge.error.location-not-found'));
            return;
        }
        onComplete();
    }, [onComplete, deliveryLocation, t]);
    const handleSaveAddress = useCallback(async (address) => {
        if ('deliveryLocationId' in address) {
            await onSaveAddress(address);
        }
        else {
            throw new Error('Invalid address type');
        }
    }, [onSaveAddress]);
    const renderMainCard = () => (_jsxs(StepperCard, { currentStep: currentStep, showStepper: !headerStepper, actions: {
            back: {
                label: t('recharge.button.back'),
                onClick: handleBack,
            },
            next: {
                label: t('recharge.button.next'),
                onClick: handleNext,
                disabled: !deliveryLocation?.locationId || isLoading,
            },
        }, children: [headerStepper, _jsxs("div", { className: styles.stepContent, "data-testid": 'viewDeliveryAddressStepContent', children: [_jsx("div", { className: styles.cardIllustration, "data-testid": 'viewHiringAddressCardIllustration', children: _jsx("img", { src: benefitsHiringCard, alt: t('hiring.card.alt'), className: styles.cardImage, "data-testid": 'imageHiringAddressBenefitsCard' }) }), _jsx(Alert, { message: t('recharge.deliveryAddress.addressChangeWarning'), appearance: 'warning', alertType: 'inline', hideCloseButton: true, alignItems: 'center', density: 'compact', testIdIdentifier: 'DeliveryAddressChangeWarningAlert' }), _jsx(AddressCard, { loading: isLoading, isSaving: isSavingAddress, isSimulating: isSimulating, title: t('deliveryAddress'), modalTitle: t('deliveryAddress'), showEditButton: true, showAddressListModal: isMultipleAddressesEnabled, location: deliveryLocation ?? undefined, onSave: handleSaveAddress, onSelectAddress: onSelectAddress, onSaveSuccess: onUpdateAddressSuccess, onSaveError: onUpdateAddressError, testIdIdentifier: 'DeliveryAddressAddressCard' })] })] }));
    const renderSummaryCard = () => (_jsx(NewRechargeSidebar, { currentStep: NewRechargeStep.DELIVERY_ADDRESS, formData: formData, onStepChange: onStepChange }));
    return (_jsx(ContentLayout, { breadcrumbs: breadcrumbs, sidebar: renderSummaryCard(), "data-testid": 'viewDeliveryAddressContainer', children: renderMainCard() }));
}
